import { useState } from 'react';
import { useElementScrollAnimation } from '../../../hooks/useScrollAnimation';
import GalleryModal from './GalleryModal';

const stats = [
  { value: '98%', label: '수강생 만족도' },
  { value: '24+', label: '24시간 연습실 개방' },
  { value: '5+', label: '전문 강사진' },
  { value: '1:1', label: '체력 및 식단관리' },
];

const features = [
  {
    icon: 'ri-user-heart-line',
    title: '입시전문 케어 / 소수정예 맞춤 지도',
    desc: '한 반 최대 6명의 소수정예 수업으로 강사의 집중적인 1:1 피드백을 받을 수 있습니다. 학생 개개인의 성향과 목표에 맞는 맞춤형 레슨을 제공합니다.',
  },
  {
    icon: 'ri-film-line',
    title: '현직 배우 강사진',
    desc: 'TV 드라마·영화·무대에서 현재도 활발히 활동 중인 현직 배우들이 직접 지도합니다. 살아있는 현장 경험을 그대로 수업에 녹여냅니다.',
  },
  {
    icon: 'ri-trophy-line',
    title: '검증된 입시 합격 성과',
    desc: '한국예술종합학교 2년연속 수시1차합격. SAC대회 2년연속 본선진출 및 고양예고 뮤지컬대회 입상등 체계적인 입시 전략으로 목표 학교 합격을 함께 만들어갑니다.',
  },
  {
    icon: 'ri-team-line',
    title: '실전 모의 오디션',
    desc: '실제 오디션과 동일한 환경에서 정기적으로 모의 오디션을 진행합니다. 긴장감 속에서도 자신의 실력을 100% 발휘할 수 있도록 반복 훈련합니다.',
  },
  {
    icon: 'ri-star-line',
    title: '연 1회 정기공연',
    desc: '매년 정기공연을 통해 공연 연습부터 무대 연출까지 실전을 직접 경험합니다. 현장 경험을 통한 배움으로 무대 위에서의 자신감과 실력을 동시에 키워갑니다.',
  },
  {
    icon: 'ri-heart-line',
    title: '즐겁고 따뜻한 학원 문화',
    desc: '서로 응원하고 함께 성장하는 따뜻한 학원 문화를 만들어갑니다. 입시생부터 취미반까지, 누구나 편안하게 연기의 즐거움을 느낄 수 있는 공간입니다.',
  },
];

export default function ScheduleSection() {
  const [galleryOpen, setGalleryOpen] = useState(false);
  const containerRef = useElementScrollAnimation();

  return (
    <>
      <section id="schedule" className="py-24 bg-neutral-950" ref={containerRef}>
        <div className="max-w-7xl mx-auto px-6">

          {/* Header */}
          <div className="text-center mb-16 animate-on-scroll">
            <span className="text-red-500 text-xs font-semibold tracking-widest uppercase mb-4 block">Why Choose Us</span>
            <h2 className="font-serif-kr font-black text-white text-4xl md:text-5xl mb-4">우리가 특별한 이유</h2>
            <p className="text-neutral-400 text-sm max-w-xl mx-auto leading-relaxed">
              찬연기아카데미만의 차별화된 교육 철학과 시스템으로<br />
              여러분의 꿈을 현실로 만들어 드립니다.
            </p>
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-4 mb-16 animate-on-scroll">
            {stats.map((stat, i) => (
              <div key={i} className="rounded-2xl p-6 text-center border border-yellow-300/60" style={{background: 'linear-gradient(135deg, #FFD700 0%, #FFC200 40%, #FFE566 70%, #FFD700 100%)'}}>
                <p className="font-serif-kr font-black text-yellow-900 text-4xl mb-1">{stat.value}</p>
                <p className="text-yellow-800 text-xs font-semibold">{stat.label}</p>
              </div>
            ))}
          </div>

          {/* Feature Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mb-16">
            {features.map((feat, i) => (
              <div
                key={i}
                className="bg-neutral-900 border border-yellow-600/60 rounded-2xl p-6 hover:border-yellow-400 transition-all duration-300 animate-on-scroll"
                style={{ animationDelay: `${i * 80}ms` }}
              >
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-red-900/30 mb-4">
                  <i className={`${feat.icon} text-red-400 text-xl`} />
                </div>
                <h3 className="font-serif-kr font-bold text-white text-lg mb-3">{feat.title}</h3>
                <p className="text-neutral-500 text-sm leading-relaxed">{feat.desc}</p>
              </div>
            ))}
          </div>

          {/* Gallery Banner */}
          <div className="animate-on-scroll">
            <button
              onClick={() => setGalleryOpen(true)}
              className="relative w-full rounded-2xl overflow-hidden group cursor-pointer bg-neutral-900 border border-neutral-800 hover:border-red-900 transition-all duration-300"
            >
              <div className="flex items-center px-12 py-14">
                <div className="text-left">
                  <p className="text-red-400 text-xs font-semibold tracking-widest uppercase mb-2">Gallery</p>
                  <h3 className="font-serif-kr font-black text-white text-3xl md:text-4xl mb-3">학원 둘러보기</h3>
                  <p className="text-neutral-400 text-sm mb-5">찬연기아카데미의 공간과 수업 현장을 직접 확인해보세요.</p>
                  <span className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-white text-black text-sm font-semibold group-hover:bg-neutral-200 transition-colors whitespace-nowrap">
                    사진 보기 <i className="ri-arrow-right-line" />
                  </span>
                </div>
                <div className="ml-auto pl-12 flex gap-3 opacity-60 group-hover:opacity-80 transition-opacity">
                  <div className="w-24 h-24 rounded-xl overflow-hidden">
                    <img src="https://static.readdy.ai/image/0227718025500d4b5e94dda25ab00f4a/d0e37f09015cd61aab5c69596d219369.jpeg" alt="" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="w-24 h-24 rounded-xl overflow-hidden">
                    <img src="https://storage.readdy-site.link/project_files/6ba0cd05-38f2-4780-9d35-22df294407b6/ead26d3e-f499-4062-b7b8-1c25e1dbd7d1_KakaoTalk_20231108_151330778.jpg?v=d03059112f5401981972bb74312ee94a" alt="" className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="w-24 h-24 rounded-xl overflow-hidden">
                    <img src="https://storage.readdy-site.link/project_files/6ba0cd05-38f2-4780-9d35-22df294407b6/c2cf9857-4939-4e7c-bbdd-ffe9fdde114b_KakaoTalk_20231108_151330778_01.jpg?v=ee77e9d116504f7632e947f46df9ba62" alt="" className="w-full h-full object-cover object-top" />
                  </div>
                </div>
              </div>
            </button>
          </div>

        </div>
      </section>

      {galleryOpen && <GalleryModal onClose={() => setGalleryOpen(false)} />}
    </>
  );
}
